import { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Check, X, RotateCcw, Link2 } from "lucide-react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { SITE_IMAGES } from "@/lib/data";
import { useContent, useSiteImage } from "@/context/ContentContext";

const input = "w-full rounded-lg border border-[#2C2C2C]/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#C9A84C]";
const btn = "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50";
const PAGES = [...new Set(SITE_IMAGES.map((s) => s.page))];

function Thumb({ src, label, testId, highlight }) {
  return (
    <figure className="flex-1">
      <figcaption className={`mb-1.5 text-xs uppercase tracking-wider ${highlight ? "text-[#9A7B2C]" : "text-[#2C2C2C]/50"}`}>{label}</figcaption>
      <div className={`aspect-[4/3] overflow-hidden rounded-lg bg-[#FAF6EF] ${highlight ? "ring-2 ring-[#C9A84C]" : "border border-[#2C2C2C]/10"}`}>
        <img src={src} alt={label} data-testid={testId} className="h-full w-full object-cover" />
      </div>
    </figure>
  );
}

function SlotCard({ slot, current, isCustom, onSaved }) {
  const [pending, setPending] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const id = slot.key;

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Veuillez choisir une image"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPending(r.data.url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async (value, msg) => {
    setBusy(true);
    try {
      await api.put("/content/images", { slot: id, url: value });
      await onSaved();
      setPending(""); setUrl("");
      toast.success(msg);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reset = () => { if (window.confirm("Revenir à l'image d'origine ?")) save(null, "Image d'origine rétablie"); };

  return (
    <div className="rounded-xl border border-[#2C2C2C]/10 bg-white p-5" data-testid={`image-slot-${id}`}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-medium text-[#2C2C2C]">{slot.label}</h3>
          <p className="text-xs text-[#2C2C2C]/50">{isCustom ? "Image personnalisée" : "Image d'origine"}</p>
        </div>
        {isCustom && !pending && (
          <button onClick={reset} disabled={busy} data-testid={`image-reset-${id}`} className="inline-flex items-center gap-1 text-xs text-[#2C2C2C]/60 hover:text-[#2C2C2C]">
            <RotateCcw size={13} /> Image d'origine
          </button>
        )}
      </div>

      <div className="flex gap-3">
        <Thumb src={current} label={pending ? "Actuelle" : "Image publiée"} testId={`image-current-${id}`} />
        {pending && <Thumb src={mediaUrl(pending)} label="Nouvelle (aperçu)" testId={`image-preview-${id}`} highlight />}
      </div>

      {pending ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => save(pending, "Image publiée sur le site")} disabled={busy} data-testid={`image-publish-${id}`} className={`${btn} bg-[#2C2C2C] text-white hover:bg-[#C9A84C]`}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Publier
          </button>
          <button onClick={() => setPending("")} disabled={busy} data-testid={`image-cancel-${id}`} className={`${btn} border border-[#2C2C2C]/20 text-[#2C2C2C] hover:border-[#2C2C2C]`}>
            <X size={15} /> Annuler
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          <label className={`${btn} cursor-pointer border border-[#C9A84C] text-[#2C2C2C] hover:bg-[#C9A84C] hover:text-white`} data-testid={`image-replace-${id}`}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {busy ? "Téléversement…" : "Remplacer"}
            <input type="file" accept="image/*" className="hidden" disabled={busy} data-testid={`image-file-${id}`} onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
          </label>
          <div className="flex gap-2">
            <input className={input} placeholder="… ou collez un lien https://" value={url} onChange={(e) => setUrl(e.target.value)} data-testid={`image-url-${id}`} />
            <button onClick={() => url.startsWith("https://") ? setPending(url.trim()) : toast.error("Le lien doit commencer par https://")} data-testid={`image-url-preview-${id}`} className={`${btn} shrink-0 border border-[#2C2C2C]/20 text-[#2C2C2C]`}>
              <Link2 size={14} /> Aperçu
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminImages() {
  const { content, refresh } = useContent();
  const img = useSiteImage();
  const custom = content?.images || {};

  return (
    <div data-testid="admin-images">
      <h1 className="font-serif text-3xl text-[#2C2C2C]">Images du site</h1>
      <p className="mt-1 text-sm text-[#2C2C2C]/60">Remplacez les images des pages : choisissez un fichier, vérifiez l'aperçu, puis publiez. Format paysage conseillé, 15 Mo maximum.</p>
      {PAGES.map((page) => (
        <section key={page} className="mt-10">
          <h2 className="mb-4 font-serif text-xl text-[#2C2C2C]">{page}</h2>
          <div className="grid gap-5 lg:grid-cols-2">
            {SITE_IMAGES.filter((s) => s.page === page).map((s) => (
              <SlotCard key={s.key} slot={s} current={img(s.key)} isCustom={!!custom[s.key]} onSaved={refresh} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
